import { WebTeamsPhotoFirebaseService } from './WebTeamsPhotoFirebaseService';
import { getDatabase } from 'firebase/database';
import { databasePaths } from '../config/firebase';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';

/**
 * Web-specific TeamsPhotoService that provides Firebase database instance
 * to the shared library TeamsPhotoFirebaseService
 */
export class WebTeamsPhotoService {
    private static instance: WebTeamsPhotoService;
    private teamsPhotoFirebaseService: WebTeamsPhotoFirebaseService;
    private isInitialized: boolean = false;
    private currentUser: User | null = null;

    private constructor() {
        this.teamsPhotoFirebaseService = new WebTeamsPhotoFirebaseService();
        this.setupAuthListener();

        // Check if user is already authenticated (for page refreshes)
        this.checkCurrentAuthState();
    }

    /**
     * Check current authentication state (for page refreshes)
     */
    private checkCurrentAuthState(): void {
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (currentUser) {
            console.log('[WebTeamsPhotoService] User already authenticated on service creation:', {
                uid: currentUser.uid,
                email: currentUser.email,
                displayName: currentUser.displayName
            });
            this.currentUser = currentUser;
            this.initializeFirebaseConnection();
        } else {
            console.log('[WebTeamsPhotoService] No user authenticated on service creation');
        }
    }

    /**
 * Setup Firebase authentication listener
 */
    private setupAuthListener(): void {
        const auth = getAuth();
        console.log('[WebTeamsPhotoService] Setting up Firebase auth listener...');
        console.log('[WebTeamsPhotoService] Current auth state:', auth.currentUser);
        console.log('[WebTeamsPhotoService] Auth instance:', auth);
        console.log('[WebTeamsPhotoService] Auth app:', auth.app);

        onAuthStateChanged(auth, (user) => {
            console.log('[WebTeamsPhotoService] Auth state changed:', user ? `User ID: ${user.uid}` : 'No user');
            if (user) {
                console.log('[WebTeamsPhotoService] User details:', {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    providerData: user.providerData
                });
            }
            this.currentUser = user;
            if (user) {
                console.log('[WebTeamsPhotoService] User authenticated, initializing Firebase connection');
                this.initializeFirebaseConnection();
            } else {
                console.log('[WebTeamsPhotoService] User not authenticated');
                this.isInitialized = false;
            }
        });
    }

    /**
 * Initialize Firebase connection when user is authenticated
 */
    private initializeFirebaseConnection(): void {
        try {
            console.log('[WebTeamsPhotoService] Initializing Firebase connection...');
            const database = getDatabase();
            console.log('[WebTeamsPhotoService] Database instance:', database);
            console.log('[WebTeamsPhotoService] Database app:', database.app);
            console.log('[WebTeamsPhotoService] Database URL:', database.app.options.databaseURL);

            this.teamsPhotoFirebaseService.setDatabase(database);

            // Set the correct database path for the environment
            const teamsPhotosPath = databasePaths.teamsPhotos();
            console.log('[WebTeamsPhotoService] Database path:', teamsPhotosPath);
            this.teamsPhotoFirebaseService.setDatabasePath(teamsPhotosPath);

            this.isInitialized = true;
            console.log('[WebTeamsPhotoService] Firebase connection initialized successfully');
        } catch (error) {
            console.error('[WebTeamsPhotoService] Failed to initialize Firebase connection:', error);
        }
    }

    /**
     * Get singleton instance
     */
    public static getInstance(): WebTeamsPhotoService {
        if (!WebTeamsPhotoService.instance) {
            WebTeamsPhotoService.instance = new WebTeamsPhotoService();
        }
        return WebTeamsPhotoService.instance;
    }

    /**
     * Check if the service is ready to use
     */
    public isReady(): boolean {
        const ready = this.isInitialized && this.currentUser !== null;
        console.log('[WebTeamsPhotoService] Service ready check:', {
            isInitialized: this.isInitialized,
            currentUser: this.currentUser ? `User ID: ${this.currentUser.uid}` : 'No user',
            ready: ready
        });
        return ready;
    }

    /**
     * Wait for the service to be ready (useful for components that need to wait for auth)
     */
    public async waitForReady(timeoutMs: number = 10000): Promise<boolean> {
        if (this.isReady()) {
            return true;
        }

        return new Promise((resolve) => {
            const startTime = Date.now();
            const checkInterval = setInterval(() => {
                if (this.isReady()) {
                    clearInterval(checkInterval);
                    resolve(true);
                } else if (Date.now() - startTime > timeoutMs) {
                    clearInterval(checkInterval);
                    console.warn('[WebTeamsPhotoService] Timeout waiting for service to be ready');
                    resolve(false);
                }
            }, 100);
        });
    }

    /**
     * Get the underlying WebTeamsPhotoFirebaseService instance
     */
    public getTeamsPhotoFirebaseService(): WebTeamsPhotoFirebaseService {
        console.log('[WebTeamsPhotoService] getTeamsPhotoFirebaseService called');
        console.log('[WebTeamsPhotoService] Current state:', {
            isInitialized: this.isInitialized,
            currentUser: this.currentUser ? `User ID: ${this.currentUser.uid}` : 'No user',
            ready: this.isReady()
        });

        if (!this.isReady()) {
            throw new Error('TeamsPhotoService is not ready. User must be authenticated first.');
        }

        console.log('[WebTeamsPhotoService] Returning WebTeamsPhotoFirebaseService instance');
        return this.teamsPhotoFirebaseService;
    }

    /**
     * Reinitialize Firebase database connection
     */
    public reinitializeDatabase(): void {
        if (!this.currentUser) {
            console.warn('[WebTeamsPhotoService] Cannot reinitialize: user not authenticated');
            return;
        }

        try {
            const database = getDatabase();
            this.teamsPhotoFirebaseService.setDatabase(database);
            console.log('[WebTeamsPhotoService] Database connection reinitialized');
        } catch (error) {
            console.error('[WebTeamsPhotoService] Failed to reinitialize Firebase database:', error);
        }
    }
}
